import type {Journey, LocationReference} from "../../dto/journey.dto";
import type {StationGroup} from "../../dto/stationGroup.dto";
import {getStationName} from "../../stations/stations";

interface StationEndpoint {
    crs: string;
    walkMinutes?: number;
    locationName: string;
}

export interface JourneyRoute {
    id: string;
    journeyId: string;
    origin: StationEndpoint;
    destination: StationEndpoint;
    viaCrs?: string;
}

export function getStationRoutes(
    journey: Journey | undefined,
    stationGroups: StationGroup[]
): JourneyRoute[] {
    if (!journey) {
        return [];
    }

    const stationGroupsById = new Map(stationGroups.map((group) => [group.id, group]));
    const origins = getStationsForLocation(journey.origin, stationGroupsById);
    const destinations = getStationsForLocation(journey.destination, stationGroupsById);

    return origins.flatMap((origin) =>
        destinations
            .filter((destination) => destination.crs !== origin.crs)
            .flatMap((destination) => getRouteOptions(journey, origin, destination))
    );
}

function getRouteOptions(
    journey: Journey,
    origin: StationEndpoint,
    destination: StationEndpoint
): JourneyRoute[] {
    const routeId = `${journey.id}:${origin.crs}-${destination.crs}`;
    const route = {
        journeyId: journey.id,
        origin,
        destination,
    };

    if (
        journey.viaCrs === undefined ||
        journey.viaCrs === origin.crs ||
        journey.viaCrs === destination.crs
    ) {
        return [{id: routeId, ...route}];
    }

    return [
        {id: `${routeId}:direct`, ...route},
        {
            id: `${routeId}:via-${journey.viaCrs}`,
            ...route,
            viaCrs: journey.viaCrs,
        },
    ];
}

function getStationsForLocation(
    location: LocationReference,
    stationGroupsById: Map<string, StationGroup>
): StationEndpoint[] {
    if (location.type === "station" && location.groupId === undefined) {
        return [{crs: location.crs, locationName: getStationName(location.crs)}];
    }
    const group = stationGroupsById.get(location.groupId!);
    return (group?.stations ?? [])
        .filter((station) => location.type === "group" || station.crs === location.crs)
        .map((station) => ({...station, locationName: group!.name}));
}
